import { jwtDecode } from "jwt-decode";

import { type User, type DeveloperProfile } from "@securityjourney/frontend-lib-svc";
import { switchAuthScopes, retrieveTenantList } from "./authService";
import { removeUsingCognitoCookies } from "./redirectStorage";

export const REFRESH_TOKEN_PERIOD = 12 * 60 * 1000;

const CURRENT_SELECTED_TENANT = "currentSelectedTenant";
const IMPERSONATED_EMAIL = "impersonatedEmail";
const IS_SJ_ADMIN = "isSJAdmin";
const REDIRECTED_TO_ADMIN = "redirectedToAdmin";

export const cleanUserArtifacts = () => {
  localStorage.removeItem("idToken");
  localStorage.removeItem("meta");
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("isImpersonating");
  removeCurrentSelectedTenant();
  removeImpersonatedEmail();
  removeIsSjAdminFromStorage();
  removeRedirectedToAdminFromStorage();
  removeUsingCognitoCookies();
};

export const removeCurrentSelectedTenant = () => localStorage.removeItem(CURRENT_SELECTED_TENANT);
export const removeImpersonatedEmail = () => localStorage.removeItem(IMPERSONATED_EMAIL);
export const removeIsSjAdminFromStorage = () => localStorage.removeItem(IS_SJ_ADMIN);
export const removeRedirectedToAdminFromStorage = () =>
  sessionStorage.removeItem(REDIRECTED_TO_ADMIN);

export const getAuthenticated = () => !!localStorage.getItem("authToken");
export const getAuthToken = () => localStorage.getItem("authToken");
export const getIsImpersonating = () => localStorage.getItem("isImpersonating");
export const getCurrentSelectedTenant = () => localStorage.getItem(CURRENT_SELECTED_TENANT);
export const getImpersonatedEmail = () => localStorage.getItem(IMPERSONATED_EMAIL);
export const getIsSjAdminFromStorage = () => localStorage.getItem(IS_SJ_ADMIN) === "true";

export const idTokenExpired = () => {
  const token = getIdToken();
  if (token) {
    const claims = jwtDecode(token);
    if (claims.exp) {
      return claims.exp < Math.floor(Date.now() / 1000);
    }
  }
  return false;
};

export const getRefreshToken = () => localStorage.getItem("refreshToken");
export const getIdToken = () => localStorage.getItem("authToken");

export const saveSelectedTenant = (tenantName: string) =>
  localStorage.setItem(CURRENT_SELECTED_TENANT, tenantName);

export const saveImpersonatedEmail = (email: string) =>
  localStorage.setItem(IMPERSONATED_EMAIL, email);

export const setIsSjAdminToStorage = () => localStorage.setItem(IS_SJ_ADMIN, "true");

// Get role from User's tenants array, returns boolean
export const mapIsAdmin = (user: User, currentTenant: string) =>
  user.tenants?.some(
    ({ tenant, roles }) => tenant === currentTenant && roles?.includes("tenant_admin")
  );

// Check if user is archived, returns boolean
export const mapIsArchived = (directoryUser: User, railsUser: any, currentTenant: string) =>
  directoryUser.tenants?.some(tenant => tenant.tenant === currentTenant && tenant.deactivated) ||
  railsUser.archived;

// Let SJ admin visit a tenant
export const visitTenant = async (tenantName: string) => {
  saveSelectedTenant(tenantName);

  await switchAuthScopes({ sjAdmin: true, admin: tenantName });
};

// Taking SJ admin out of a tenant context and back to the SJ Admin App
export const exitTenantVisit = async () => {
  removeCurrentSelectedTenant();
  removeImpersonatedEmail();

  await switchAuthScopes({ sjAdmin: true });
};

// Let SJ admin stop impersonating a tenant admin user
export const stopImpersonating = async (tenantName: string) => {
  await switchAuthScopes({ sjAdmin: true, admin: tenantName });
  removeImpersonatedEmail();
};

export const initTenantSelection = async () => {
  const tenantList = await retrieveTenantList();

  if (tenantList.length === 1) {
    const selectedTenant = tenantList[0];
    saveSelectedTenant(selectedTenant);
    return true;
  } else {
    removeCurrentSelectedTenant();
    return false;
  }
};

export const isDeveloperProfileEmpty = (profile?: DeveloperProfile) => {
  if (!profile) return true;

  return Object.values(profile).every(
    value => value === "" || (Array.isArray(value) && value.length === 0)
  );
};
